import React, { useState } from "react";
import styled from "styled-components";
import { useReviewerCandidates, useCRACandidates, pmLabel } from "../usePMCandidates";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 440px;
  max-width: 95vw;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
`;

const Title = styled.h3`
  margin: 0 0 20px;
  font-size: 16px;
  color: #212529;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #495057;
`;

const Input = styled.input`
  padding: 7px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13px;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const Select = styled.select`
  padding: 7px 10px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
  &:focus { outline: none; border-color: #0d6efd; }
  &:disabled { background: #f8f9fa; color: #adb5bd; }
`;

const ErrorText = styled.span`
  font-size: 11px;
  color: #dc3545;
`;

const CraList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 168px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px 10px;
`;

const CraRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #212529;
  cursor: pointer;
`;

const Hint = styled.span`
  font-size: 11px;
  color: #adb5bd;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const Btn = styled.button`
  padding: 7px 18px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  background: ${(p) => (p.$primary ? "#0d6efd" : "#6c757d")};
  color: #fff;
  &:disabled { opacity: 0.5; }
`;

export default function FeasibilityCreateModal({ onSubmit, onClose }) {
  const [form, setForm] = useState({
    studyCode: "", studyTitle: "", reviewerEmployeeNo: "", craEmployeeNos: [],
  });
  const [saving, setSaving] = useState(false);
  // 후보 목록에서 본인은 이미 빠져 있다 — 스터디를 만든 사람이 Owner가 되고, Owner는
  // 자기 스터디의 Reviewer도 CRA도 될 수 없다(될 필요도 없다).
  const { candidates: reviewers, loading: rvLoading, error: rvError } = useReviewerCandidates();
  const { candidates: cras,      loading: craLoading, error: craError } = useCRACandidates();

  // CRAs are optional — the owner can add them later. Reviewer is required.
  const complete = form.studyCode && form.studyTitle && form.reviewerEmployeeNo;

  function toggleCRA(employeeNo) {
    setForm((p) => {
      const has = p.craEmployeeNos.includes(employeeNo);
      return {
        ...p,
        craEmployeeNos: has
          ? p.craEmployeeNos.filter((no) => no !== employeeNo)
          : [...p.craEmployeeNos, employeeNo],
      };
    });
  }

  async function handleSubmit() {
    if (!complete) return;
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Backdrop>
      <Modal>
        <Title>New Feasibility Questionnaire</Title>

        <Field>
          <Label>Study Code *</Label>
          <Input
            value={form.studyCode}
            maxLength={50}
            onChange={(e) => setForm((p) => ({ ...p, studyCode: e.target.value }))}
            placeholder="e.g. ABC-001"
          />
        </Field>
        <Field>
          <Label>Study Title *</Label>
          <Input
            value={form.studyTitle}
            maxLength={200}
            onChange={(e) => setForm((p) => ({ ...p, studyTitle: e.target.value }))}
            placeholder="Full study title"
          />
        </Field>

        <Field>
          <Label>Reviewer *</Label>
          <Select
            value={form.reviewerEmployeeNo}
            disabled={rvLoading || !!rvError}
            onChange={(e) => setForm((p) => ({ ...p, reviewerEmployeeNo: e.target.value }))}
          >
            <option value="">
              {rvLoading ? "불러오는 중..." : "내부검토·발행을 승인할 Reviewer를 선택해주세요"}
            </option>
            {reviewers.map((emp) => (
              <option key={emp.employeeNo} value={emp.employeeNo}>
                {pmLabel(emp)} — {emp.jd}
              </option>
            ))}
          </Select>
          {rvError && <ErrorText>{rvError}</ErrorText>}
          {!rvLoading && !rvError && reviewers.length === 0 && (
            <ErrorText>선택 가능한 Reviewer(SFQ Reviewer 권한 보유자)가 없습니다.</ErrorText>
          )}
        </Field>

        <Field>
          <Label>CRA <Hint>(여러 명 선택 가능, 나중에 추가/변경 가능)</Hint></Label>
          {craLoading ? (
            <Hint>불러오는 중...</Hint>
          ) : craError ? (
            <ErrorText>{craError}</ErrorText>
          ) : cras.length === 0 ? (
            <ErrorText>선택 가능한 CRA(SFQ CRA 권한 보유자)가 없습니다.</ErrorText>
          ) : (
            <CraList>
              {cras.map((emp) => (
                <CraRow key={emp.employeeNo}>
                  <input
                    type="checkbox"
                    checked={form.craEmployeeNos.includes(emp.employeeNo)}
                    onChange={() => toggleCRA(emp.employeeNo)}
                  />
                  {pmLabel(emp)}{emp.jd ? ` — ${emp.jd}` : ""}
                </CraRow>
              ))}
            </CraList>
          )}
        </Field>

        <Footer>
          <Btn onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn
            $primary
            onClick={handleSubmit}
            disabled={!complete || saving}
          >
            {saving ? "Creating..." : "Create"}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
